import React, { useEffect, useState } from 'react'
import { Tooltip } from '@mui/material'
import Button from '@mui/material/Button'
import AttachFileIcon from '@mui/icons-material/AttachFile'
import { useGetKycArchiveAttachmentUrlLazyQuery } from '../../../api-services/kyc-archive/kycArchiveApi'
import { type AcuantAttachmentDto, type AcuantSignedUrlDto } from '../../../api-schema-types/kyc-archive/KycArchiveDtos'
import { openInNewTab } from '../../../utility/dom/DomWindowHelpers'
import { setNotification } from '../../components/ui-widgets/uiWidgetsSlice'
import { RtkQueryHelper } from '../../../api-services/shared/RtkQueryHelper'
import { useAppDispatch } from '../../../app/hooks'

interface ComponentProps {
  attachment: AcuantAttachmentDto
}

export const KycArchiveAttachmentButton: React.FC<ComponentProps> = ({ attachment }: ComponentProps) => {
  const dispatch = useAppDispatch()

  const [signedUrl, setSignedUrl] = useState<string | null>(null)
  const [apiGetAttachmentUrlTrigger] = useGetKycArchiveAttachmentUrlLazyQuery()

  const handleOpenAttachment = async (): Promise<void> => {
    apiGetAttachmentUrlTrigger(attachment.id)
      .unwrap()
      .then((triggerResult) => {
        const url = (triggerResult as AcuantSignedUrlDto).s3Url
        if (url === '') {
          dispatch(setNotification({
            open: true,
            severity: 'warning',
            message: 'Attachments are not accessible from this environment'
          }))
          return
        }
        setSignedUrl(url)
      })
      .catch((err) => {
        dispatch(setNotification({ open: true, severity: 'error', message: RtkQueryHelper.mapResponseError(err) }))
      })
  }

  useEffect(() => {
    if (signedUrl !== null) {
      openInNewTab(signedUrl)
    }
  }, [signedUrl])

  return (
    <Tooltip title={attachment.filePresent ? 'Open attachment in new tab' : 'File missing from archive'}>
      <span>
        <Button
          variant='outlined'
          size='small'
          startIcon={<AttachFileIcon />}
          disabled={!attachment.filePresent}
          onClick={async () => { await handleOpenAttachment() }}
        >
          {attachment.attachedFilename ?? `Attachment ${attachment.id}`}
        </Button>
      </span>
    </Tooltip>
  )
}
